import { useNavigate } from "react-router-dom";
import { Workforce } from "@/types/workforce";
import { Badge } from "@/components/ui/badge";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

interface WorkforceListProps {
	workforce: Workforce[];
}

const getStatusBadgeVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
	switch (status) {
		case 'active':
			return 'default';
		case 'on_hold':
			return 'secondary';
		case 'completed':
			return 'outline';
		case 'terminated':
			return 'destructive';
		default:
			return 'secondary';
	}
};

const formatStatus = (status: string) => {
	return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

const formatDate = (dateStr: string | null | undefined) => {
	if (!dateStr) return '-';
	try {
		return format(new Date(dateStr), 'MMM d, yyyy');
	} catch {
		return dateStr;
	}
};

export const WorkforceList = ({ workforce }: WorkforceListProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const handleRowClick = (id: string) => {
		navigate(`/workforce/${id}`);
	};

	return (
		<div className="rounded-sm border">
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>{t("workforce.fields.consultant", "Consultant")}</TableHead>
						<TableHead>{t("workforce.fields.company", "Company")}</TableHead>
						<TableHead>{t("workforce.fields.role", "Role")}</TableHead>
						<TableHead>{t("workforce.fields.startDate", "Start Date")}</TableHead>
						<TableHead>{t("workforce.fields.endDate", "End Date")}</TableHead>
						<TableHead>{t("workforce.fields.location", "Location")}</TableHead>
						<TableHead>{t("workforce.fields.status", "Status")}</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{workforce.map((item) => (
						<TableRow
							key={item.id}
							className="cursor-pointer hover:bg-muted/50"
							onClick={() => handleRowClick(item.id)}
						>
							<TableCell className="font-medium">
								{item.candidate?.name || 'Unknown'}
							</TableCell>
							<TableCell>
								{item.job?.company?.name || item.external_company_name || '-'}
							</TableCell>
							<TableCell>
								{item.job?.title || item.external_job_title || '-'}
							</TableCell>
							<TableCell>{formatDate(item.start_date)}</TableCell>
							<TableCell>
								{item.end_date ? formatDate(item.end_date) :
								 item.expected_end_date ? `${formatDate(item.expected_end_date)} (exp.)` :
								 'Ongoing'}
							</TableCell>
							<TableCell>
								{item.location || '-'}
								{item.location_type && (
									<span className="ml-1 text-xs text-muted-foreground">
										({item.location_type})
									</span>
								)}
							</TableCell>
							<TableCell>
								<Badge variant={getStatusBadgeVariant(item.status)}>
									{formatStatus(item.status)}
								</Badge>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	);
};
